// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { useAuthStore } from './stores/authStore';
import { ToastContainer } from './components/ui/Toast';

// Eager load critical components
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';

// Lazy load page components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'));
const SummariesPage = lazy(() => import('./pages/SummariesPage'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const PushNotificationsPage = lazy(() => import('./pages/PushNotificationsPage'));
const AIGeneratorPage = lazy(() => import('./pages/AIGeneratorPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const WorkerLogsPage = lazy(() => import('./pages/WorkerLogsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Loading component for Suspense fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:userId" element={<UserDetailPage />} />
            <Route path="summaries" element={<SummariesPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="push-notifications" element={<PushNotificationsPage />} />
            <Route path="ai-generator" element={<AIGeneratorPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="worker-logs" element={<WorkerLogsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
